import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, ArrowUp, ArrowDown, Star, Eye, EyeOff, Upload, Trash2, Plus, Image as ImageIcon, Palette } from "lucide-react";

const TOKEN = () => localStorage.getItem("cadii_admin_token") || "";
const AUTH = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${TOKEN()}` });

const CATEGORY_COLORS = {
  ROUND: "bg-[#1B4D3E]/10 text-[#1B4D3E]",
  SCORECARD: "bg-blue-100 text-blue-700",
  HOLE: "bg-amber-100 text-amber-700",
  MATCH: "bg-purple-100 text-purple-700",
};
const TABS = [
  { key: "templates", label: "Templates", icon: Sparkles },
  { key: "stickers", label: "Stickers", icon: ImageIcon },
  { key: "styles", label: "Estilos", icon: Palette },
];

export default function AdminSocialTemplates() {
  const [tab, setTab] = useState("templates");
  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="admin-social-title">
          <Sparkles className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} /> CADII Stories
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Configura los diseños, stickers y estilos que ven los jugadores.</p>
      </div>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-stories-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#1B4D3E] text-[#1B4D3E]" : "border-transparent text-[#94A3B8] hover:text-[#1B4D3E]"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "templates" && <TemplatesTab />}
      {tab === "stickers" && <StickersTab />}
      {tab === "styles" && <StylesTab />}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/* ---------------- Templates ---------------- */
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/social/admin/templates`, { credentials: "include", headers: AUTH() });
      if (res.ok) setTemplates((await res.json()).templates);
      else toast.error("No autorizado");
    } catch {
      toast.error("Error al cargar templates");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, body) => {
    setSaving(id);
    try {
      const res = await fetch(`${API}/social/admin/templates/${id}`, { method: "PUT", credentials: "include", headers: AUTH(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving("");
    }
  };

  const toggleActive = async (t) => {
    setTemplates((p) => p.map((x) => (x.template_id === t.template_id ? { ...x, active: !x.active } : x)));
    await update(t.template_id, { active: !t.active });
  };
  const toggleFeatured = async (t) => {
    setTemplates((p) => p.map((x) => (x.template_id === t.template_id ? { ...x, featured: !x.featured } : x)));
    await update(t.template_id, { featured: !t.featured });
  };
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= templates.length) return;
    const list = [...templates];
    [list[index], list[target]] = [list[target], list[index]];
    const reordered = list.map((t, i) => ({ ...t, order: i + 1 }));
    setTemplates(reordered);
    await Promise.all([update(reordered[index].template_id, { order: reordered[index].order }), update(reordered[target].template_id, { order: reordered[target].order })]);
  };
  const saveEdit = async () => {
    setTemplates((p) => p.map((x) => (x.template_id === editing.template_id ? editing : x)));
    await update(editing.template_id, { name: editing.name, subtitle: editing.subtitle, category: editing.category });
    setEditing(null);
    toast.success("Template actualizado");
  };

  if (loading) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="social-templates-list">
      {templates.map((t, index) => (
        <Card key={t.template_id} data-testid={`social-template-row-${t.template_id}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(index, -1)} disabled={index === 0 || !!saving} className="text-[#94A3B8] hover:text-[#1B4D3E] disabled:opacity-30" data-testid={`social-template-up-${t.template_id}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(index, 1)} disabled={index === templates.length - 1 || !!saving} className="text-[#94A3B8] hover:text-[#1B4D3E] disabled:opacity-30" data-testid={`social-template-down-${t.template_id}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 min-w-0">
              {editing?.template_id === t.template_id ? (
                <div className="space-y-2">
                  <input className="w-full text-sm border rounded-lg px-2 py-1.5" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-edit-name" placeholder="Nombre" />
                  <input className="w-full text-sm border rounded-lg px-2 py-1.5" value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="template-edit-subtitle" placeholder="Subtítulo" />
                  <select className="w-full text-sm border rounded-lg px-2 py-1.5" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="template-edit-category">
                    {["ROUND", "SCORECARD", "HOLE", "MATCH"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="px-3 py-1.5 rounded-lg bg-[#1B4D3E] text-white text-xs font-bold" data-testid="template-edit-save">Guardar</button>
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold">Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-bold text-[#0F172A] ${!t.active ? "opacity-40" : ""}`}>{t.name}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[t.category] || "bg-gray-100 text-gray-600"}`}>{t.category}</span>
                    {t.featured && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-yellow-500 text-yellow-500" /> Destacado</span>}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{t.subtitle} · {t.formats.join(", ")}</p>
                  <button onClick={() => setEditing({ ...t })} className="text-xs text-[#1B4D3E] font-semibold mt-1" data-testid={`template-edit-${t.template_id}`}>Editar texto</button>
                </>
              )}
            </div>
            <button onClick={() => toggleFeatured(t)} disabled={!!saving} className={`p-2 rounded-lg ${t.featured ? "bg-yellow-100 text-yellow-600" : "bg-gray-50 text-[#94A3B8] hover:text-yellow-600"}`} data-testid={`social-template-featured-${t.template_id}`} title="Destacar"><Star className={`w-4 h-4 ${t.featured ? "fill-yellow-500" : ""}`} /></button>
            <button onClick={() => toggleActive(t)} disabled={!!saving} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold ${t.active ? "bg-[#1B4D3E]/10 text-[#1B4D3E]" : "bg-gray-100 text-[#94A3B8]"}`} data-testid={`social-template-active-${t.template_id}`}>{t.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}{t.active ? "Activo" : "Oculto"}</button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Stickers ---------------- */
function StickersTab() {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("Custom");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/social/admin/stickers`, { credentials: "include", headers: AUTH() });
      if (res.ok) setStickers((await res.json()).stickers);
    } catch {
      toast.error("Error al cargar stickers");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file || !label.trim()) { toast.error("Agrega imagen y nombre"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label.trim());
      form.append("category", category.trim() || "Custom");
      const res = await fetch(`${API}/social/admin/stickers`, { method: "POST", credentials: "include", headers: { Authorization: `Bearer ${TOKEN()}` }, body: form });
      if (!res.ok) throw new Error();
      toast.success("Sticker agregado");
      setLabel(""); setFile(null);
      load();
    } catch {
      toast.error("No se pudo subir");
    } finally {
      setUploading(false);
    }
  };
  const toggle = async (s) => {
    setStickers((p) => p.map((x) => (x.sticker_id === s.sticker_id ? { ...x, active: !x.active } : x)));
    await fetch(`${API}/social/admin/stickers/${s.sticker_id}`, { method: "PUT", credentials: "include", headers: AUTH(), body: JSON.stringify({ active: !s.active }) });
  };
  const remove = async (s) => {
    setStickers((p) => p.filter((x) => x.sticker_id !== s.sticker_id));
    await fetch(`${API}/social/admin/stickers/${s.sticker_id}`, { method: "DELETE", credentials: "include", headers: AUTH() });
    toast.success("Sticker eliminado");
  };

  return (
    <div>
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Nuevo sticker</p>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <label className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer overflow-hidden bg-gray-50" data-testid="sticker-file-label">
              {file ? <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-full object-contain" /> : <Upload className="w-6 h-6 text-gray-400" />}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sticker-file-input" />
            </label>
            <div className="flex-1 w-full space-y-2">
              <input className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Nombre (ej. Fuego)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="sticker-label-input" />
              <input className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Categoría (ej. Marca, Insignias)" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="sticker-category-input" />
              <button onClick={upload} disabled={uploading} className="px-4 py-2 rounded-lg bg-[#1B4D3E] text-white text-sm font-bold disabled:opacity-50" data-testid="sticker-upload-btn">{uploading ? "Subiendo…" : "Subir sticker"}</button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? <Spinner /> : stickers.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-8" data-testid="stickers-empty">Aún no hay stickers. Sube el primero arriba.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="stickers-list">
          {stickers.map((s) => (
            <Card key={s.sticker_id} data-testid={`sticker-row-${s.sticker_id}`}>
              <CardContent className="p-3">
                <div className="w-full aspect-square bg-gray-50 rounded-lg flex items-center justify-center mb-2 overflow-hidden">
                  <img src={`${API}${s.url.replace(/^\/api/, "")}`} alt={s.label} className="w-4/5 h-4/5 object-contain" />
                </div>
                <p className={`text-sm font-bold truncate ${!s.active ? "opacity-40" : ""}`}>{s.label}</p>
                <p className="text-[10px] text-gray-400 mb-2">{s.category}</p>
                <div className="flex gap-2">
                  <button onClick={() => toggle(s)} className={`flex-1 text-xs font-bold py-1.5 rounded-lg ${s.active ? "bg-[#1B4D3E]/10 text-[#1B4D3E]" : "bg-gray-100 text-gray-500"}`} data-testid={`sticker-toggle-${s.sticker_id}`}>{s.active ? "Activo" : "Oculto"}</button>
                  <button onClick={() => remove(s)} className="p-1.5 rounded-lg bg-red-50 text-red-500" data-testid={`sticker-delete-${s.sticker_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Styles ---------------- */
function StylesTab() {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [cardBg, setCardBg] = useState("#103B31");
  const [text, setText] = useState("#F4ECD8");
  const [accent, setAccent] = useState("#9CC24A");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/social/admin/styles`, { credentials: "include", headers: AUTH() });
      if (res.ok) setStyles((await res.json()).styles);
    } catch {
      toast.error("Error al cargar estilos");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error("Ponle un nombre"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/social/admin/styles`, { method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify({ name: name.trim(), card_bg: cardBg, text, accent }) });
      if (!res.ok) throw new Error();
      toast.success("Estilo creado");
      setName("");
      load();
    } catch {
      toast.error("No se pudo crear");
    } finally {
      setSaving(false);
    }
  };
  const toggle = async (s) => {
    setStyles((p) => p.map((x) => (x.style_id === s.style_id ? { ...x, active: !x.active } : x)));
    await fetch(`${API}/social/admin/styles/${s.style_id}`, { method: "PUT", credentials: "include", headers: AUTH(), body: JSON.stringify({ active: !s.active }) });
  };
  const remove = async (s) => {
    setStyles((p) => p.filter((x) => x.style_id !== s.style_id));
    await fetch(`${API}/social/admin/styles/${s.style_id}`, { method: "DELETE", credentials: "include", headers: AUTH() });
    toast.success("Estilo eliminado");
  };

  return (
    <div>
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Nuevo estilo</p>
          <input className="w-full text-sm border rounded-lg px-3 py-2 mb-3" placeholder="Nombre (ej. Neón)" value={name} onChange={(e) => setName(e.target.value)} data-testid="style-name-input" />
          <div className="flex gap-4 mb-3 flex-wrap">
            <ColorField label="Fondo tarjeta" value={cardBg} onChange={setCardBg} testId="style-color-cardbg" />
            <ColorField label="Texto" value={text} onChange={setText} testId="style-color-text" />
            <ColorField label="Acento" value={accent} onChange={setAccent} testId="style-color-accent" />
            <div className="flex-1 min-w-[140px]">
              <p className="text-[11px] text-gray-500 mb-1">Vista previa</p>
              <div className="rounded-xl p-3" style={{ backgroundColor: cardBg }}>
                <div className="text-2xl font-black" style={{ color: text }}>70</div>
                <div className="text-sm font-black" style={{ color: accent }}>-2 · H12</div>
              </div>
            </div>
          </div>
          <button onClick={create} disabled={saving} className="px-4 py-2 rounded-lg bg-[#1B4D3E] text-white text-sm font-bold disabled:opacity-50" data-testid="style-create-btn">{saving ? "Guardando…" : "Crear estilo"}</button>
        </CardContent>
      </Card>

      {loading ? <Spinner /> : styles.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-8" data-testid="styles-empty">Aún no hay estilos personalizados. Los jugadores tienen Crema, Verde y Oscuro por default.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="styles-list">
          {styles.map((s) => (
            <Card key={s.style_id} data-testid={`style-row-${s.style_id}`}>
              <CardContent className="p-3">
                <div className="rounded-xl p-3 mb-2" style={{ backgroundColor: s.card_bg }}>
                  <div className="text-2xl font-black" style={{ color: s.text }}>70</div>
                  <div className="text-sm font-black" style={{ color: s.accent }}>-2 · H12</div>
                </div>
                <p className={`text-sm font-bold truncate ${!s.active ? "opacity-40" : ""}`}>{s.name}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => toggle(s)} className={`flex-1 text-xs font-bold py-1.5 rounded-lg ${s.active ? "bg-[#1B4D3E]/10 text-[#1B4D3E]" : "bg-gray-100 text-gray-500"}`} data-testid={`style-toggle-${s.style_id}`}>{s.active ? "Activo" : "Oculto"}</button>
                  <button onClick={() => remove(s)} className="p-1.5 rounded-lg bg-red-50 text-red-500" data-testid={`style-delete-${s.style_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="text-[11px] text-gray-500 mb-1">{label}</p>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-14 h-10 rounded-lg border cursor-pointer" data-testid={testId} />
    </div>
  );
}
